// Client-side split-test analytics helpers.
//
// Reads the active variant + session from sessionStorage (assigned on /apply
// load — see src/routes/apply.tsx) and fires non-blocking inserts to
// variant_events / variant_submissions. All writes are best-effort; failures
// are swallowed so they never break the visitor experience.
//
// Preview mode (admin previewing a variant in isolation) sets a flag in
// sessionStorage that suppresses ALL writes — we don't want admin previews
// polluting the analytics.

export const VARIANT_ID_KEY = "inflow_variant_assignment";
export const SESSION_ID_KEY = "inflow_session_id";
export const SURVEY_START_FLAG = "inflow_survey_start_fired";
export const PREVIEW_MODE_KEY = "inflow_preview_mode";
/** Persistent (localStorage) per-device id — lets us count unique people,
 *  not just per-tab sessions. Falls back to the session id if unavailable. */
export const VISITOR_ID_KEY = "inflow_visitor_id";

export function getVisitorId(): string | null {
  if (typeof window === "undefined") return null;
  try {
    let id = localStorage.getItem(VISITOR_ID_KEY);
    if (!id) {
      id = crypto.randomUUID();
      localStorage.setItem(VISITOR_ID_KEY, id);
    }
    return id;
  } catch {
    return null;
  }
}

/**
 * Internal traffic must never enter the funnel numbers:
 *  - admin previews (PREVIEW_MODE_KEY), and
 *  - anyone with an admin session in this browser (the team checking the page).
 * This is what made views/visitors look inflated before launch.
 */
export function isInternalTraffic(): boolean {
  if (typeof window === "undefined") return true;
  try {
    if (sessionStorage.getItem(PREVIEW_MODE_KEY) === "1") return true;
    for (let i = 0; i < localStorage.length; i += 1) {
      const k = localStorage.key(i) ?? "";
      if (k.startsWith("sb-") && k.endsWith("-auth-token") && localStorage.getItem(k)) return true;
    }
  } catch {
    // storage unavailable — treat as normal visitor traffic
  }
  return false;
}

export function getAnalyticsIdentity(): {
  variantId: string;
  sessionId: string;
  visitorId: string;
} | null {
  const c = ctx();
  if (!c) return null;
  return {
    variantId: c.variantId,
    sessionId: c.sessionId,
    visitorId: getVisitorId() ?? c.sessionId,
  };
}


export type VariantEventType =
  | "page_view"
  | "cta_click"
  | "survey_start"
  | "survey_complete";

function ctx(): { variantId: string; sessionId: string } | null {
  if (typeof window === "undefined") return null;
  if (isInternalTraffic()) return null;
  const variantId = sessionStorage.getItem(VARIANT_ID_KEY);
  const sessionId = sessionStorage.getItem(SESSION_ID_KEY);
  if (!variantId || !sessionId) return null;
  return { variantId, sessionId };
}

export async function logEvent(
  eventType: VariantEventType,
  metadata?: Record<string, unknown>,
): Promise<void> {
  const c = ctx();
  if (!c) return;
  // Written through a same-origin server function: direct Data API inserts
  // from the browser get blocked for a chunk of paid traffic, which made
  // clicks/starts under-count vs server-written completions.
  //
  // Retried with backoff because a dropped milestone event permanently skews
  // the funnel. Milestone writes are idempotent server-side (unique index), so
  // a retry can never double-count.
  const payload = {
    variant_id: c.variantId,
    variant_label:
      typeof metadata?.['label'] === "string"
        ? (metadata['label'] as string)
        : typeof metadata?.['variant'] === "string"
          ? (metadata['variant'] as string)
          : null,
    session_id: c.sessionId,
    event_type: eventType,
    event_metadata: {
      ...(metadata ?? {}),
      visitor_id: getVisitorId() ?? c.sessionId,
    },
  };

  for (let attempt = 0; attempt < 3; attempt += 1) {
    try {
      const { logVariantEvent } = await import("@/lib/track.functions");
      const res = await logVariantEvent({ data: payload });
      if (res && res.ok) return;
    } catch (e) {
      if (attempt === 2) console.error("[analytics] logEvent failed", eventType, e);
    }
    await new Promise((r) => setTimeout(r, 400 * (attempt + 1)));
  }
}


export async function logSubmission(inquiryId: string | null, contactId: string | null): Promise<void> {
  // Kept as a no-op for legacy callers. Durable submissions are written in
  // submitSurveyLead on the server; a second browser write can race redirects
  // and double-count one registration.
  void inquiryId;
  void contactId;
}

/** Fire survey_start at most once per session. */
export function markSurveyStartOnce(): boolean {
  if (typeof window === "undefined") return false;
  if (sessionStorage.getItem(SURVEY_START_FLAG) === "1") return false;
  sessionStorage.setItem(SURVEY_START_FLAG, "1");
  return true;
}
